import { useEffect, useMemo, useRef, useState } from "react"
import { computed } from "mobx"
import { observer } from "mobx-react"
import { Form, GetProp, Progress, UploadFile, UploadProps } from "antd"
import { UploadChangeParam } from "antd/es/upload"
import { UploadRef } from "antd/es/upload/Upload"
import Dragger from "antd/lib/upload/Dragger"
import { choose } from "choose-matching"

import { ImageUpload } from "@src/shared/componemts/ImageUploadUI/entity/ImageUpload"
import { Uploaded } from "@src/shared/componemts/ImageUploadUI/entity/Uploaded"
import { ImageUploadDefaultPlaceholder } from "@src/shared/componemts/ImageUploadUI/ui/ImageUploadDedaultPlaceholder"
import { ImageUploadIsLoading } from "@src/shared/componemts/ImageUploadUI/ui/ImageUploadIsLoading"
import { ImageUploadPreview } from "@src/shared/componemts/ImageUploadUI/ui/ImageUploadPreview"
import { UploadItemRenderCsv } from "@src/shared/componemts/ImageUploadUI/ui/UploadItemRenderCsv"
import css from "./index.module.css"

type FileType = Parameters<GetProp<UploadProps, "beforeUpload">>[0]
type Props = {
  accept?: string
  control: ImageUpload | Uploaded
  disabled?: boolean
  onChange?: (
    file: UploadChangeParam<UploadFile<any>>["fileList"] | null,
  ) => void
  placeholder?: JSX.Element
}
export const ImageUploaderUI = observer(
  ({
    accept = ".jpg,.jpeg,.png",
    control,
    disabled = false,
    onChange,
    placeholder = <ImageUploadDefaultPlaceholder accept={accept} />,
  }: Props) => {
    const ref = useRef<UploadRef<any>>(null)

    const [fileList, setFileList] = useState<UploadFile[]>([])

    useEffect(() => {
      const intervalId = setInterval(() => {
        if (ref.current) {
          control.setRef(ref)

          clearInterval(intervalId)
        }
      })

      return () => clearInterval(intervalId)
    }, [ref.current])

    const change = async (e: any) => {
      control.onChange(e).then(() => {
        setFileList([...e.fileList])

        if (onChange) {
          onChange(e.fileList)
        }
      })
    }

    const remove = () => {
      control.remove()

      if (onChange) {
        onChange(null)
      }
    }

    const showImagePreview = computed(
      () =>
        control instanceof ImageUpload && control.preview && !control.isLoading,
    ).get()

    return (
      <div className={css.ImageUploadUI}>
        <Dragger
          accept={accept}
          beforeUpload={() => false}
          className={css.ImageUploadUI_upload}
          disabled={disabled}
          fileList={fileList}
          itemRender={(_, file, fileList, { remove }) => {
            return <UploadItemRenderCsv name={file.name} remove={remove} />
          }}
          listType="text"
          maxCount={1}
          onChange={(e) => change(e)}
          onRemove={() => remove()}
          ref={ref}
          showUploadList={!(control instanceof ImageUpload)}
        >
          {choose(
            [
              control.isLoading,
              <ImageUploadIsLoading progress={control.progress} />,
            ],
            [!showImagePreview, placeholder],
            [
              Boolean(showImagePreview),
              <ImageUploadPreview
                control={control as ImageUpload}
                onRemove={remove}
              />,
            ],
          )}
        </Dragger>
      </div>
    )
  },
)
